import React from 'react';
import styled from 'styled-components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 16px 0;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: none;
  background-color: ${(props) => (props.active ? '#01A8CE' : '#ddd')};
  color: white;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #007ea7;
  }
`;

const Ellipsis = styled.span`
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
`;

const getPaginationRange = (currentPage: number, totalPages: number) => {
  const range = 2; // Number of pages to show before and after the current page
  let start = Math.max(1, currentPage - range);
  let end = Math.min(totalPages, currentPage + range);

  if (currentPage - range <= 1) {
    end = Math.min(totalPages, end + (range - (currentPage - 1)));
  } else if (currentPage + range >= totalPages) {
    start = Math.max(1, start - (range - (totalPages - currentPage)));
  }

  const paginationRange: number[] = [];
  for (let i = start; i <= end; i++) {
    paginationRange.push(i);
  }

  return paginationRange;
};

const CustomPagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  const paginationRange = getPaginationRange(currentPage, totalPages);

  return (
    <PaginationContainer>
      {currentPage > 1 && (
        <PageButton onClick={() => onPageChange(currentPage - 1)}>Prev</PageButton>
      )}

      {paginationRange[0] > 1 && (
        <>
          <PageButton onClick={() => onPageChange(1)}>1</PageButton>
          {paginationRange[0] > 2 && <Ellipsis>...</Ellipsis>}
        </>
      )}

      {paginationRange.map((page) => (
        <PageButton
          key={page}
          active={page === currentPage}
          onClick={() => onPageChange(page)}
        >
          {page}
        </PageButton>
      ))}

      {paginationRange[paginationRange.length - 1] < totalPages && (
        <>
          {paginationRange[paginationRange.length - 1] < totalPages - 1 && <Ellipsis>...</Ellipsis>}
          <PageButton onClick={() => onPageChange(totalPages)}>{totalPages}</PageButton>
        </>
      )}

      {currentPage < totalPages && (
        <PageButton onClick={() => onPageChange(currentPage + 1)}>Next</PageButton>
      )}
    </PaginationContainer>
  );
};

export default CustomPagination;
